import { Check, CircleAlert, Copy, Download, ShieldAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Page,
  PageHeader,
  RadioGroup,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@aeris/ui'

import { isDesktop } from '../lib/desktop.ts'

/**
 * O Aeris instalado, oferecido pelo próprio produto (F043, F067).
 *
 * ## Por que uma tela e não um link no rodapé
 *
 * Instalar isto tem momentos em que uma pessoa desiste, e todos acontecem
 * antes de o Aeris abrir: o **SmartScreen** dizendo "o Windows protegeu o
 * computador" (o instalador não é assinado, e essa tela parece um vírus); o
 * **UAC** pedindo senha de administrador; e a primeira abertura **perguntando
 * o endereço do servidor**, que quem não souber responder para aí.
 *
 * Um link solto não explica nenhum. Esta tela explica todos, na ordem em que
 * aparecem.
 *
 * ## Por que abas, e por que a aba não baixa nada (F067)
 *
 * Antes, os botões dos DOIS sistemas apareciam sempre e o tutorial era de UM
 * só, escolhido por `looksLikeLinux()`. Quem estava no Linux e clicava em
 * "Baixar para Windows" — o caso real da loja, levar num pendrive até o caixa
 * — recebia instruções de Ubuntu para um `.exe`. O palpite também era
 * invisível: não havia como dizer "não, quero o outro".
 *
 * A aba resolve os dois: ela É o palpite, visível e trocável, e o painel
 * inteiro (requisitos, botão e tutorial) muda junto — nunca mais um botão de
 * um sistema com o passo a passo de outro.
 *
 * **A aba não dispara o download.** As abas do Radix ativam com as setas do
 * teclado (`activationMode` padrão é `'automatic'`), então uma seta baixaria
 * 130 MB sem ninguém ter pedido. Quem baixa é o botão.
 *
 * O Radix desmonta o painel inativo, e aqui isso é o comportamento certo: os
 * dois tutoriais se contradizem, e um Ctrl+F que achasse os dois seria pior
 * que não achar nenhum.
 */

/**
 * O padrão aponta para o repositório público de releases.
 *
 * O código-fonte é privado, e um asset de release de repositório privado exige
 * autenticação para baixar — o site precisaria guardar um token do repositório
 * inteiro em produção para servir um arquivo. O instalador vai para um
 * repositório separado e público: ele é um shell que pergunta o endereço do
 * servidor, sem dado de cliente e sem segredo.
 */
const RELEASES_BASE =
  import.meta.env.VITE_DESKTOP_RELEASES_URL ??
  'https://github.com/thzzin/aeris-releases/releases/latest/download'

const WINDOWS_URL =
  import.meta.env.VITE_DESKTOP_DOWNLOAD_URL ??
  `${RELEASES_BASE}/Aeris-One-Setup.exe`

/**
 * Os dois formatos do Linux, porque as duas respostas são legítimas.
 *
 * O `.deb` instala de verdade: entra no menu de aplicativos, atualiza pelo
 * gerenciador de pacotes, e é o que se quer no computador do balcão. O
 * `.AppImage` não instala nada — é um arquivo só, que roda com dois cliques —
 * e é o que serve para experimentar numa máquina que não é sua, ou numa
 * distribuição que não usa `.deb`.
 *
 * Eles não são a mesma instalação com outra extensão: o `.deb` pede senha de
 * administrador e o AppImage nunca pede. Até a F067 os dois caminhos estavam
 * espremidos no mesmo passo, e o passo seguinte dizia "ele vai pedir a senha
 * de administrador" para quem tinha escolhido o AppImage. Por isso o formato
 * é uma escolha explícita, e o tutorial segue a escolha.
 */
const DEB_URL =
  import.meta.env.VITE_DESKTOP_DEB_URL ?? `${RELEASES_BASE}/aeris-one_amd64.deb`

const APPIMAGE_URL =
  import.meta.env.VITE_DESKTOP_APPIMAGE_URL ??
  `${RELEASES_BASE}/Aeris-One-x86_64.AppImage`

/**
 * O instalador existe mesmo? (F067)
 *
 * Até esta feature a tela oferecia três botões e os três davam **404**: o
 * repositório de releases não foi criado, nenhuma tag `desktop-v*` foi
 * empurrada, e `.github/workflows/desktop.yml` nunca rodou. Uma página que
 * promete um arquivo inexistente manda a pessoa para uma tela de erro do
 * GitHub, que não diz o que fazer e parece defeito do Aeris.
 *
 * **Por que uma variável de build e não uma checagem.** Descobrir isso na hora
 * exigiria um `HEAD` do navegador para o `github.com`, que o CORS barra — e a
 * release ainda redireciona para outro domínio, que também barra. Não há
 * resposta que o navegador consiga ler.
 *
 * Uma URL explícita **também** conta como publicada: quem apontou a variável
 * para um arquivo próprio tem um arquivo, e não precisa ligar mais nada.
 */
const RELEASES_PUBLICADAS =
  import.meta.env.VITE_DESKTOP_RELEASES_PUBLISHED === 'true' ||
  import.meta.env.VITE_DESKTOP_RELEASES_URL !== undefined

const publicado = (urlExplicita: string | undefined): boolean =>
  urlExplicita !== undefined || RELEASES_PUBLICADAS

const WINDOWS_PUBLICADO = publicado(import.meta.env.VITE_DESKTOP_DOWNLOAD_URL)
const DEB_PUBLICADO = publicado(import.meta.env.VITE_DESKTOP_DEB_URL)
const APPIMAGE_PUBLICADO = publicado(import.meta.env.VITE_DESKTOP_APPIMAGE_URL)

/**
 * Qual sistema mostrar primeiro.
 *
 * Só decide a aba que abre — nunca esconde a outra. A pessoa pode estar no
 * Linux baixando para levar num pendrive até o caixa que roda Windows, e esse
 * é um caso real de loja.
 */
const looksLikeLinux = (): boolean => {
  const agent = navigator.userAgent
  return agent.includes('Linux') && !agent.includes('Android')
}

type FormatoLinux = 'deb' | 'appimage'

const Passo = ({
  numero,
  titulo,
  children,
}: {
  numero: number
  titulo: string
  children: ReactNode
}): ReactNode => (
  <li className="flex gap-3">
    <span
      aria-hidden="true"
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-(--radius-full) bg-(--surface-sunken) text-(length:--text-xs) font-(--weight-semibold) text-(--text-secondary)"
    >
      {numero}
    </span>
    <div className="flex flex-col gap-1">
      <p className="font-(--weight-medium) text-(--text-primary)">{titulo}</p>
      <div className="flex flex-col gap-2 text-(length:--text-sm) text-(--text-secondary)">
        {children}
      </div>
    </div>
  </li>
)

/** Um comando para colar num terminal. Monoespaçado e selecionável de uma vez. */
const Comando = ({ children }: { children: ReactNode }): ReactNode => (
  <code className="block rounded-(--radius-md) bg-(--surface-sunken) p-2 font-mono break-all select-all">
    {children}
  </code>
)

/**
 * O endereço do servidor, com botão de copiar.
 *
 * Isto vai ser digitado ou colado numa OUTRA máquina, e um domínio errado por
 * uma letra produz um erro que não menciona a letra. O botão é o mesmo padrão
 * de `atendimento-canais.tsx`: melhor esforço, porque a Clipboard API não
 * existe fora de contexto seguro — uma loja servida em HTTP puro na LAN é um
 * caso real — e o texto continua selecionável de qualquer jeito.
 */
const EnderecoDoServidor = ({ endereco }: { endereco: string }): ReactNode => {
  const [copiado, setCopiado] = useState(false)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="min-w-0 flex-1 rounded-(--radius-md) bg-(--surface-sunken) p-2 font-mono break-all select-all">
        {endereco}
      </code>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        leadingIcon={
          copiado ? (
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <Copy aria-hidden="true" className="h-3.5 w-3.5" />
          )
        }
        onClick={() => {
          try {
            void navigator.clipboard.writeText(endereco).then(
              () => {
                setCopiado(true)
              },
              () => {
                setCopiado(false)
              }
            )
          } catch {
            setCopiado(false)
          }
        }}
      >
        {copiado ? 'Copiado' : 'Copiar'}
      </Button>
    </div>
  )
}

/**
 * O aviso de que o arquivo ainda não existe.
 *
 * Nomeia as três coisas que faltam, porque "indisponível" manda a pessoa
 * abrir um chamado e isto manda ela resolver.
 */
const AindaNaoPublicado = ({ id }: { id: string }): ReactNode => (
  <div
    id={id}
    className="flex items-start gap-2 rounded-(--radius-md) bg-(--warning-subtle) p-3 text-(length:--text-sm) text-(--text-primary)"
  >
    <CircleAlert
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
    />
    <div className="flex flex-col gap-1">
      <p className="font-(--weight-medium)">
        O instalador ainda não foi publicado.
      </p>
      <p className="text-(--text-secondary)">
        O arquivo é construído por{' '}
        <code className="font-mono">desktop.yml</code> quando uma tag{' '}
        <code className="font-mono">desktop-v0.1.0</code> é empurrada, e vai
        para um repositório público de releases. Faltam três coisas: esse
        repositório existir, a variável{' '}
        <code className="font-mono">RELEASES_REPO</code> com o segredo{' '}
        <code className="font-mono">RELEASES_TOKEN</code>, e a tag.
      </p>
    </div>
  </div>
)

const BotaoBaixar = ({
  href,
  rotulo,
  disponivel,
  descreveEm,
  variant = 'primary',
}: {
  href: string
  rotulo: string
  disponivel: boolean
  descreveEm: string
  variant?: 'primary' | 'secondary'
}): ReactNode =>
  disponivel ? (
    <Button
      asChild
      variant={variant}
      leadingIcon={<Download aria-hidden="true" className="h-4 w-4" />}
    >
      <a href={href} rel="noreferrer">
        {rotulo}
      </a>
    </Button>
  ) : (
    /*
     * Desabilitado, e com o motivo LIGADO ao botão: um botão morto sem
     * explicação lê como produto quebrado. O `aria-describedby` faz o leitor
     * de tela anunciar o aviso junto do botão, e não só para quem enxerga.
     */
    <Button
      type="button"
      variant={variant}
      disabled
      aria-describedby={descreveEm}
      leadingIcon={<Download aria-hidden="true" className="h-4 w-4" />}
    >
      {rotulo}
    </Button>
  )

const PainelWindows = ({ endereco }: { endereco: string }): ReactNode => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col gap-2">
      <p className="text-(length:--text-sm) text-(--text-secondary)">
        Windows 10 ou 11, 64 bits. Cerca de 130 MB — o instalador leva o
        WebView2 dentro, para funcionar num computador sem internet boa.
      </p>
      {WINDOWS_PUBLICADO ? null : (
        <AindaNaoPublicado id="baixar-windows-indisponivel" />
      )}
      <div className="flex flex-wrap gap-2">
        <BotaoBaixar
          href={WINDOWS_URL}
          rotulo="Baixar o instalador"
          disponivel={WINDOWS_PUBLICADO}
          descreveEm="baixar-windows-indisponivel"
        />
      </div>
    </div>

    <ol className="flex flex-col gap-4">
      <Passo numero={1} titulo="O Windows vai avisar. Pode seguir.">
        <p className="flex items-start gap-2">
          <ShieldAlert
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
          />
          <span>
            Vai aparecer <strong>“O Windows protegeu o seu computador”</strong>.
            Clique em <strong>Mais informações</strong> e depois em{' '}
            <strong>Executar assim mesmo</strong>. Esse aviso não diz que o
            programa é perigoso: diz que ele ainda não tem um certificado de
            editor, que é uma assinatura paga.
          </span>
        </p>
      </Passo>

      <Passo numero={2} titulo="Ele vai pedir a senha de administrador.">
        <p>
          O Aeris é instalado para o computador inteiro, e não para um login só
          — se o balcão tem mais de um usuário do Windows, todos encontram o
          programa. Tenha a senha de administrador à mão antes de começar.
        </p>
      </Passo>

      <Passo numero={3} titulo="Na primeira abertura, ele pergunta o servidor.">
        <p>É este endereço:</p>
        <EnderecoDoServidor endereco={endereco} />
      </Passo>

      <Passo numero={4} titulo="Entre com o seu e-mail e senha de sempre.">
        <p>
          É a mesma conta do site. A partir daí o programa continua logado mesmo
          depois de fechar — no navegador isso também vale, mas o instalado é o
          que vai funcionar sem internet.
        </p>
      </Passo>
    </ol>
  </div>
)

const PainelLinux = ({ endereco }: { endereco: string }): ReactNode => {
  const [formato, setFormato] = useState<FormatoLinux>('deb')
  const deb = formato === 'deb'

  const disponivel = deb ? DEB_PUBLICADO : APPIMAGE_PUBLICADO

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          Ubuntu 24.04 ou mais novo, 64 bits.
        </p>

        <RadioGroup<FormatoLinux>
          name="formato-linux"
          value={formato}
          onValueChange={setFormato}
          options={[
            {
              value: 'deb',
              label: '.deb — instala no sistema',
              description:
                'Entra no menu de aplicativos e atualiza pelo gerenciador de pacotes. É o que se quer no computador do balcão.',
            },
            {
              value: 'appimage',
              label: 'AppImage — roda sem instalar',
              description:
                'Um arquivo só, que abre com dois cliques. Serve para experimentar numa máquina que não é sua, ou numa distribuição que não usa .deb.',
            },
          ]}
        />

        {disponivel ? null : (
          <AindaNaoPublicado id="baixar-linux-indisponivel" />
        )}
        <div className="flex flex-wrap gap-2">
          <BotaoBaixar
            href={deb ? DEB_URL : APPIMAGE_URL}
            rotulo={deb ? 'Baixar o .deb' : 'Baixar o AppImage'}
            disponivel={disponivel}
            descreveEm="baixar-linux-indisponivel"
          />
        </div>
      </div>

      {/*
        Os dois tutoriais têm quatro passos e só os dois primeiros diferem —
        mas diferem no que importa: o `.deb` pede senha de administrador e o
        AppImage não pede nada. Manter os dois numa lista só, como era antes da
        F067, é o que fazia o passo 2 mentir para metade das pessoas.
      */}
      <ol className="flex flex-col gap-4">
        {deb ? (
          <Passo numero={1} titulo="Abra o arquivo baixado.">
            <p>
              O <strong>.deb</strong> abre no instalador de aplicativos com dois
              cliques. Pelo terminal, é:
            </p>
            <Comando>sudo apt install ./aeris-one_amd64.deb</Comando>
            <p className="flex items-start gap-2">
              <ShieldAlert
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
              />
              <span>
                Se o sistema reclamar de bibliotecas faltando, instale{' '}
                <code className="font-mono">libwebkit2gtk-4.1-0</code>. Ela já
                vem no Ubuntu 24.04 e mais novos.
              </span>
            </p>
          </Passo>
        ) : (
          <Passo numero={1} titulo="Marque como executável e abra.">
            <p>
              O <strong>AppImage</strong> não se instala — é o programa inteiro
              num arquivo. O sistema não o executa enquanto ele não tiver
              permissão:
            </p>
            <Comando>chmod +x Aeris-One-x86_64.AppImage</Comando>
            <p className="flex items-start gap-2">
              <ShieldAlert
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
              />
              <span>
                Se o sistema reclamar de bibliotecas faltando, instale{' '}
                <code className="font-mono">libwebkit2gtk-4.1-0</code>. Ela já
                vem no Ubuntu 24.04 e mais novos.
              </span>
            </p>
          </Passo>
        )}

        {deb ? (
          <Passo numero={2} titulo="Ele vai pedir a senha de administrador.">
            <p>
              O <strong>.deb</strong> instala para o computador inteiro, então o
              sistema pede a sua senha — como qualquer programa que entra no
              menu de aplicativos.
            </p>
          </Passo>
        ) : (
          <Passo numero={2} titulo="Guarde o arquivo num lugar fixo.">
            <p>
              O AppImage não pede senha nenhuma: ele roda como o seu usuário,
              sem instalar. Em troca, ele não entra no menu de aplicativos e não
              se atualiza — <strong>apagar o arquivo é desinstalar</strong>.
              Deixe-o na pasta pessoal, não em Downloads.
            </p>
          </Passo>
        )}

        <Passo
          numero={3}
          titulo="Na primeira abertura, ele pergunta o servidor."
        >
          <p>É este endereço:</p>
          <EnderecoDoServidor endereco={endereco} />
        </Passo>

        <Passo numero={4} titulo="Entre com o seu e-mail e senha de sempre.">
          <p>
            É a mesma conta do site. A partir daí o programa continua logado
            mesmo depois de fechar — no navegador isso também vale, mas o
            instalado é o que vai funcionar sem internet.
          </p>
        </Passo>
      </ol>
    </div>
  )
}

export const BaixarPage = (): ReactNode => {
  /*
   * Quem já está no aplicativo instalado não precisa instalá-lo.
   *
   * A tela continua existindo — ela é o roteiro para instalar nos OUTROS
   * computadores da loja, que é o caso real: a pessoa está no PC do balcão e
   * quer o Aeris no caixa 2.
   */
  const noDesktop = isDesktop()

  /*
   * O palpite é lido uma vez, na montagem. Depois disso quem manda é a aba —
   * se a pessoa trocou, ela quer o outro sistema, e um re-render não pode
   * desfazer essa escolha.
   */
  const [abaInicial] = useState(() => (looksLikeLinux() ? 'linux' : 'windows'))

  const endereco = window.location.origin

  return (
    <Page>
      <PageHeader
        title="Aeris no computador"
        description="O mesmo Aeris, instalado. É ele que vai continuar vendendo quando a internet da loja cair."
      />

      {noDesktop ? (
        <p className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
          <Check aria-hidden="true" className="h-4 w-4 text-(--success)" />
          Você já está no aplicativo instalado. Use esta página para instalar
          nos outros computadores da loja.
        </p>
      ) : null}

      <Tabs defaultValue={abaInicial}>
        <TabsList aria-label="Sistema operacional">
          <TabsTrigger value="windows">Windows</TabsTrigger>
          <TabsTrigger value="linux">Linux</TabsTrigger>
        </TabsList>

        <TabsContent value="windows" className="mt-4">
          <PainelWindows endereco={endereco} />
        </TabsContent>

        <TabsContent value="linux" className="mt-4">
          <PainelLinux endereco={endereco} />
        </TabsContent>
      </Tabs>
    </Page>
  )
}
